// src/pages/LitEssaySection.jsx
//
// Student-facing render of a SAMPLE ESSAY lit_section (sectionKind 'essay'). Faithful port
// of the prototype LitSampleEssaysView (between-the-lines-v2.html L12872-12938): an exam
// prompt, an L3/L4/L5 level toggle, and the model response with inline examiner-comment
// highlights (hover a highlighted snippet -> floating examiner note).
//
// Reads ESSAY_BLOCKS (litSectionShape.js, mirrors LitEssayEditor): blocks.category (text),
// blocks.prompt (longText), blocks.l3/l4/l5 (longText), blocks.comments ([{snippet,note}]).
// The essay TITLE is the section title (content.title).
//
// MODEL DIVERGENCE (noted): the prototype view received the WHOLE essay list and rendered an
// in-component essay selector + category filter. In our model each essay is its OWN
// lit_section item, and the reader's left sub-list already navigates between essays (like
// Characters) -- so this component renders a SINGLE essay and omits that selector/filter.
//
// The prototype literature essay view is INLINE (level toggle + comment highlight), NOT a
// modal -- so unlike the set-text reader's SetTextPreview EssayModal, there is no modal to
// mirror here. Deferred like the Overview/Slice-2 ports: glossary highlighting (the prompt
// renders as plain text) and Export PDF. Examiner-comment highlighting is core to this view
// (not glossary) and IS ported.
import React, { Fragment, useState } from 'react';
import GlossTerm from '../components/InlineGlossPopover.jsx';
import LessonHeader from '../components/reader/LessonHeader.jsx';

const LEVELS = ['l3', 'l4', 'l5'];

export default function LitEssaySection({ content, done = false }) {
    const blocks = (content && content.blocks) || {};
    const title = (content && content.title) || 'Sample Essay';
    const category = typeof blocks.category === 'string' ? blocks.category : '';
    const prompt = typeof blocks.prompt === 'string' ? blocks.prompt : '';
    const comments = Array.isArray(blocks.comments) ? blocks.comments : [];
    const [level, setLevel] = useState('l4');
    const [showComments, setShowComments] = useState(true);

    const body = typeof blocks[level] === 'string' ? blocks[level] : '';

    // Split the response on each examiner-comment snippet and wrap matches in an ANCHORED,
    // tappable note popover (GlossTerm -- shows next to the word, hover + tap, mobile-OK).
    // Comments with an EMPTY note are skipped (no blank popover, no stray highlight).
    const renderBody = (text) => {
        if (!showComments || comments.length === 0) return text;
        let remaining = text || '';
        const parts = [];
        let key = 0;
        comments.forEach((c) => {
            const snippet = c && c.snippet;
            const note = c && typeof c.note === 'string' ? c.note.trim() : '';
            if (!snippet || !note) return;
            const idx = remaining.indexOf(snippet);
            if (idx >= 0) {
                parts.push(<Fragment key={key++}>{remaining.slice(0, idx)}</Fragment>);
                parts.push(
                    <GlossTerm key={key++} label={'Examiner note on ' + snippet} popover={
                        <>
                            <span className="block font-display text-book-accent text-xs mb-1 font-semibold">Examiner&rsquo;s note</span>
                            <span className="block font-serif text-book-text text-sm leading-relaxed">{note}</span>
                        </>
                    }>{snippet}</GlossTerm>
                );
                remaining = remaining.slice(idx + snippet.length);
            }
        });
        parts.push(<Fragment key={key++}>{remaining}</Fragment>);
        return parts;
    };

    return (
        <div className="max-w-3xl mx-auto pb-8 relative">
            <LessonHeader eyebrow={'Sample essays' + (category ? ` · ${category}` : '')}
                title={title} blocks={blocks} done={done} />
            <p className="font-serif italic text-book-muted text-base -mt-4 mb-6">L3 · L4 · L5 comparisons with examiner notes.</p>

            {prompt && (
                <div className="rounded-[var(--radius-card)] border-2 border-book-accent/50 bg-book-accent/5 p-4 mb-4">
                    <div className="font-display text-book-muted text-xs mb-2 font-semibold">Exam question</div>
                    <p className="font-display text-book-text text-base leading-relaxed font-semibold mb-2 whitespace-pre-wrap">{prompt}</p>
                </div>
            )}

            <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
                <div className="inline-flex border border-book-accent/40 bg-book-pageDark/50 overflow-hidden">
                    {LEVELS.map((l) => (
                        <button key={l} onClick={() => setLevel(l)}
                            className={`px-4 py-2 font-display text-xs transition-colors font-semibold ${level === l ? 'bg-book-accent text-book-pageDark' : 'text-book-muted hover:text-book-accent'}`}>
                            {l.toUpperCase()}
                        </button>
                    ))}
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={showComments} onChange={(e) => setShowComments(e.target.checked)} />
                    <span className="font-serif italic text-book-muted text-sm">Show examiner comments</span>
                </label>
            </div>

            <div className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4">
                <div className="font-display text-book-muted text-xs mb-3 font-semibold">Level {level.slice(1).toUpperCase()} response</div>
                {body
                    ? <p className="font-serif text-book-text text-base leading-relaxed text-justify whitespace-pre-wrap">{renderBody(body)}</p>
                    : <p className="font-serif italic text-book-muted text-sm">No Level {level.slice(1).toUpperCase()} response yet.</p>}
            </div>

        </div>
    );
}
